import React from 'react';
import { Modal, Form, Input, Select, InputNumber } from 'antd';
import { t } from '../../../../i18n';
import { PhoneInput, CountryCodeAndPhoneNumber } from 'components/PhoneInput';
import { ROLE_OPTIONS } from 'constants/roleOptions';
import ImageUploader from 'components/ImageUploader';
import { passwordPolicyRule, PASSWORD_MAX_LENGTH } from 'utils/passwordValidator';

export interface TmsTaskConfirmDriverVehicleModalProps {
    open: boolean;
    onOk: () => void | Promise<void>;
    onCancel: () => void;
    title: React.ReactNode;
    confirmLoading?: boolean;
    isDriver1FreeInput: boolean;
    /** 司机2 也是手输：同样要建司机账号，字段与司机1 一致 */
    isDriver2FreeInput: boolean;
    isVehicleFreeInput: boolean;
    driver1Form: ReturnType<typeof Form.useForm>[0];
    driver2Form: ReturnType<typeof Form.useForm>[0];
    vehicleForm: ReturnType<typeof Form.useForm>[0];
    carrierName: string;
    driverVehicleOptions: { label: string; value: number }[];
    driverVehicleLoading?: boolean;
    vehicleModelOptions: { label: string; value: number }[];
    fuelTypeOptions: { label: string; value: number }[];
    statusOptions: { value: number; label: string }[];
    onVehicleModelChange: (vehicleModelId: number) => void | Promise<void>;
    zIndex: number;
}

const TmsTaskConfirmDriverVehicleModal: React.FC<TmsTaskConfirmDriverVehicleModalProps> = ({
    open,
    onOk,
    onCancel,
    title,
    confirmLoading = false,
    isDriver1FreeInput,
    isDriver2FreeInput,
    isVehicleFreeInput,
    driver1Form,
    driver2Form,
    vehicleForm,
    carrierName,
    driverVehicleOptions,
    driverVehicleLoading = false,
    vehicleModelOptions,
    fuelTypeOptions,
    statusOptions,
    onVehicleModelChange,
    zIndex,
}) => {
    // 可见栏数：司机1 / 司机2 / 车辆，最多三栏。宽度在原基准上收窄 1/5：单栏 520，多栏每栏再 +400（520/920/1320）
    const sectionCount = [isDriver1FreeInput, isDriver2FreeInput, isVehicleFreeInput].filter(Boolean).length;
    const isMultiSection = sectionCount > 1;
    const sectionClassName = isMultiSection ? 'flex-1' : 'w-full';

    /**
     * 一栏司机信息。司机1 与司机2 字段基本一致，差别只在车辆：
     * 司机2 不绑定车辆，车辆行仍然展示（两栏对齐、字段一目了然），但只占位显示 "-" 且不进表单值。
     */
    const renderDriverSection = (
        form: ReturnType<typeof Form.useForm>[0],
        sectionTitle: string,
        bindVehicle: boolean
    ) => (
        <div className={sectionClassName}>
            <h4 className="text-lg font-semibold border-l-4 border-purple-600 pl-2">
                {sectionTitle}
            </h4>
            <div className="font-semibold mb-4 text-base border-b pb-2"></div>
            <Form
                form={form}
                layout="horizontal"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
            >
                <Form.Item
                    label={String(t('tms.account.pageDetails.phone'))}
                    name="phone"
                    rules={[
                        {
                            validator: (_, value: CountryCodeAndPhoneNumber) => {
                                if (!value || !value.phoneNumber) {
                                    return Promise.reject(new Error(String(t('tms.account.messages.phoneNumberRequired'))));
                                }
                                const phoneRegex = /^\d+$/;
                                if (!phoneRegex.test(value.phoneNumber)) {
                                    return Promise.reject(new Error(String(t('tms.account.messages.phoneNumberDigitsOnly'))));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                >
                    <PhoneInput disabled />
                </Form.Item>
                <Form.Item
                    label={String(t('tms.account.pageDetails.password'))}
                    name="password"
                    rules={[
                        { required: true, message: String(t('tms.account.messages.pleaseInputPassword')) },
                        passwordPolicyRule(String(t('accountSecurity.passwordNotMeet')))
                    ]}
                >
                    <Input.Password
                        placeholder={String(t('accountSecurity.passwordHint'))}
                        maxLength={PASSWORD_MAX_LENGTH}
                    />
                </Form.Item>
                <Form.Item
                    label={String(t('tms.account.pageDetails.name'))}
                    name="name"
                    rules={[{ required: true, message: String(t('tms.account.messages.pleaseInputName')) }]}
                >
                    <Input placeholder={String(t('tms.account.placeholders.inputPlaceholder'))} disabled />
                </Form.Item>
                <Form.Item
                    label={String(t('tms.account.pageDetails.email'))}
                    name="email"
                    rules={[{ type: 'email', message: String(t('tms.account.messages.pleaseInputValidEmail')) }]}
                >
                    <Input placeholder={String(t('tms.account.placeholders.inputPlaceholder'))} />
                </Form.Item>
                <Form.Item
                    label={String(t('tms.account.pageDetails.personnelStatus'))}
                    name="status"
                >
                    <Select options={statusOptions} placeholder={String(t('tms.account.pageDetails.select'))} disabled />
                </Form.Item>
                <Form.Item
                    label={String(t('tms.account.pageDetails.role'))}
                    name="role"
                >
                    <Select disabled options={ROLE_OPTIONS} placeholder="select" />
                </Form.Item>
                <Form.Item
                    label={String(t('tms.vehicle.pageDetails.carrier'))}
                >
                    <Input value={carrierName} disabled />
                </Form.Item>
                {bindVehicle ? (
                    <Form.Item
                        label={String(t('tms.account.pageDetails.vehicle'))}
                        name={isVehicleFreeInput ? 'vehicleDisplay' : 'vehicleId'}
                    >
                        {isVehicleFreeInput ? (
                            <Input disabled />
                        ) : (
                            <Select
                                loading={driverVehicleLoading}
                                placeholder={String(t('tms.account.placeholders.selectVehicleOptional'))}
                                disabled
                                allowClear
                                optionLabelProp="label"
                                options={driverVehicleOptions.map(option => ({
                                    ...option,
                                    // 仅显示车牌号（label 原格式为 `vehicleNumber - licensePlate`）
                                    label: typeof option.label === 'string'
                                        ? option.label.split(' - ').slice(-1)[0]
                                        : option.label
                                }))}
                            />
                        )}
                    </Form.Item>
                ) : (
                    // 司机2 不绑车：不给 name，值不进表单、不会被提交，只占位显示 "-"
                    <Form.Item label={String(t('tms.account.pageDetails.vehicle'))}>
                        <Input value="-" disabled />
                    </Form.Item>
                )}
            </Form>
        </div>
    );

    return (
        <Modal
            title={<span className="text-black font-bold">{title}</span>}
            open={open}
            onOk={onOk}
            onCancel={onCancel}
            okText={String(t('tms.task.confirmModal.confirm'))}
            cancelText={String(t('tms.task.confirmModal.cancel'))}
            width={520 + (sectionCount - 1) * 400}
            confirmLoading={confirmLoading}
            zIndex={zIndex}
        >
            <div className={`flex ${isMultiSection ? 'gap-6' : ''}`}>
                {isDriver1FreeInput && renderDriverSection(
                    driver1Form,
                    String(t('tms.task.confirmModal.driver1Info')),
                    true
                )}
                {isDriver2FreeInput && renderDriverSection(
                    driver2Form,
                    String(t('tms.task.confirmModal.driver2Info')),
                    false
                )}

                {isVehicleFreeInput && (
                    <div className={sectionClassName}>
                        <h4 className="text-lg font-semibold border-l-4 border-purple-600 pl-2">
                            {String(t('tms.task.confirmModal.vehicleInfo'))}
                        </h4>
                        <div className="font-semibold mb-4 text-base border-b pb-2"></div>
                        <Form
                            form={vehicleForm}
                            layout="horizontal"
                            labelCol={{span: 8}}
                            wrapperCol={{span: 16}}
                        >
                            <Form.Item
                                name="licensePlate"
                                label={String(t('tms.vehicle.pageDetails.licensePlate'))}
                                rules={[{
                                    required: true,
                                    message: String(t('tms.vehicle.messages.pleaseInputLicensePlate'))
                                }]}
                            >
                                <Input placeholder={String(t('tms.vehicle.placeholders.inputLicensePlate'))} disabled/>
                            </Form.Item>
                            <Form.Item
                                label={String(t('tms.vehicle.pageDetails.carrier'))}
                            >
                                <Input value={carrierName} disabled/>
                            </Form.Item>
                            <Form.Item
                                name="fuelType"
                                label={String(t('tms.vehicle.pageDetails.fuelType'))}
                                rules={[{
                                    required: true,
                                    message: String(t('tms.vehicle.messages.pleaseSelectFuelType'))
                                }]}
                            >
                                <Select options={fuelTypeOptions}
                                        placeholder={String(t('tms.vehicle.placeholders.selectFuelType'))}/>
                            </Form.Item>
                            <Form.Item
                                name="vehicleTypeId"
                                label={String(t('tms.vehicle.pageDetails.model'))}
                                rules={[{required: true, message: String(t('tms.vehicle.messages.pleaseSelectModel'))}]}
                            >
                                <Select
                                    options={vehicleModelOptions}
                                    placeholder={String(t('tms.vehicle.placeholders.selectModel'))}
                                    onChange={(id: number) => onVehicleModelChange(id)}
                                    disabled
                                />
                            </Form.Item>
                            <Form.Item
                                name="maxWeightLbs"
                                label={String(t('tms.vehicle.pageDetails.maxWeightLbs'))}
                                rules={[{
                                    required: true,
                                    type: 'number',
                                    message: String(t('tms.vehicle.messages.pleaseInputPayload'))
                                }]}
                            >
                                <InputNumber min={0.01} max={99999999.99} precision={2} style={{width: '100%'}}
                                             placeholder={String(t('tms.vehicle.placeholders.inputPayload'))}/>
                            </Form.Item>
                            <Form.Item
                                name="maxVolumeFt3"
                                label={String(t('tms.vehicle.pageDetails.maxVolumeFt3'))}
                                rules={[{
                                    required: true,
                                    type: 'number',
                                    message: String(t('tms.vehicle.messages.pleaseInputCapacity'))
                                }]}
                            >
                                <InputNumber min={0.01} max={9999999.999} precision={3} style={{width: '100%'}}
                                             placeholder={String(t('tms.vehicle.placeholders.inputCapacity'))}/>
                            </Form.Item>
                            <Form.Item
                                name="lengthFt"
                                label={String(t('tms.vehicle.pageDetails.lengthFt'))}
                                rules={[{
                                    required: true,
                                    type: 'number',
                                    message: String(t('tms.vehicle.messages.pleaseInputLength'))
                                }]}
                            >
                                <InputNumber min={0.01} max={999999.99} precision={2} style={{width: '100%'}}
                                             placeholder={String(t('tms.vehicle.placeholders.inputLength'))}/>
                            </Form.Item>
                            <Form.Item
                                name="widthFt"
                                label={String(t('tms.vehicle.pageDetails.widthFt'))}
                                rules={[{
                                    required: true,
                                    type: 'number',
                                    message: String(t('tms.vehicle.messages.pleaseInputWidth'))
                                }]}
                            >
                                <InputNumber min={0.01} max={999999.99} precision={2} style={{width: '100%'}}
                                             placeholder={String(t('tms.vehicle.placeholders.inputWidth'))}/>
                            </Form.Item>
                            <Form.Item
                                name="heightFt"
                                label={String(t('tms.vehicle.pageDetails.heightFt'))}
                                rules={[{
                                    required: true,
                                    type: 'number',
                                    message: String(t('tms.vehicle.messages.pleaseInputHeight'))
                                }]}
                            >
                                <InputNumber min={0.01} max={999999.99} precision={2} style={{width: '100%'}}
                                             placeholder={String(t('tms.vehicle.placeholders.inputHeight'))}/>
                            </Form.Item>
                            <Form.Item
                                name="status"
                                label={String(t('tms.vehicle.pageDetails.status'))}
                            >
                                <Select options={statusOptions}
                                        placeholder={String(t('tms.vehicle.placeholders.select'))} disabled/>
                            </Form.Item>
                            <Form.Item
                                name="vehiclePhotos"
                                label={String(t('tms.vehicle.pageDetails.vehiclePhoto'))}
                            >
                                <ImageUploader
                                    value={[]}
                                    displayUrls={[]}
                                    onChange={(urls: string[]) => vehicleForm.setFieldsValue({vehiclePhotos: urls})}
                                    maxCount={3}
                                />
                            </Form.Item>
                        </Form>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default TmsTaskConfirmDriverVehicleModal;
